import { motion } from 'framer-motion';
import { Sparkles, Building2, Users } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const content = {
  nl: {
    eyebrow: 'Voor wie',
    title: 'Wij bouwen websites voor bedrijven die professioneel online willen groeien',
    subtitle: 'Netblitz werkt vooral voor zelfstandigen, KMO\'s en dienstverleners die nood hebben aan een representatieve website zonder technische rompslomp.',
    features: [
      {
        icon: Sparkles,
        title: 'Schoonmaakbedrijven',
        description: 'Toon je diensten, werkregio\'s, referenties en laat bezoekers eenvoudig een offerte aanvragen.'
      },
      {
        icon: Building2,
        title: 'Dienstenbedrijven',
        description: 'Van facility en onderhoud tot consultancy, techniek en zorg: wij maken websites die duidelijk en betrouwbaar overkomen.'
      },
      {
        icon: Users,
        title: 'Zelfstandigen & KMO\'s',
        description: 'Ideaal voor ondernemers die snel online willen staan met een website die vertrouwen opwekt en leads oplevert.'
      }
    ]
  },
  en: {
    eyebrow: 'For whom',
    title: 'We build websites for businesses that want to grow professionally online',
    subtitle: 'Netblitz works mainly for freelancers, SMEs and service providers who need a representative website without technical hassle.',
    features: [
      {
        icon: Sparkles,
        title: 'Cleaning companies',
        description: 'Show your services, work areas, references and let visitors easily request a quote.'
      },
      {
        icon: Building2,
        title: 'Service companies',
        description: 'From facility and maintenance to consultancy, technology and healthcare: we create websites that are clear and reliable.'
      },
      {
        icon: Users,
        title: 'Freelancers & SMEs',
        description: 'Ideal for entrepreneurs who want to get online quickly with a website that builds trust and generates leads.'
      }
    ]
  },
  fr: {
    eyebrow: 'Pour qui',
    title: 'Nous créons des sites web pour les entreprises qui veulent grandir en ligne',
    subtitle: 'Netblitz travaille principalement pour les indépendants, PME et prestataires de services.',
    features: [
      {
        icon: Sparkles,
        title: 'Entreprises de nettoyage',
        description: 'Montrez vos services, zones de travail, références et permettez aux visiteurs de demander un devis.'
      },
      {
        icon: Building2,
        title: 'Entreprises de services',
        description: 'De la facility au conseil: nous créons des sites web clairs et fiables.'
      },
      {
        icon: Users,
        title: 'Indépendants & PME',
        description: 'Idéal pour les entrepreneurs qui veulent être en ligne rapidement avec un site qui génère des leads.'
      }
    ]
  },
  de: {
    eyebrow: 'Für wen',
    title: 'Wir erstellen Websites für Unternehmen, die professionell online wachsen wollen',
    subtitle: 'Netblitz arbeitet hauptsächlich für Selbstständige, KMU und Dienstleister.',
    features: [
      {
        icon: Sparkles,
        title: 'Reinigungsunternehmen',
        description: 'Zeigen Sie Ihre Dienste, Arbeitsgebiete, Referenzen und lassen Sie Besucher einfach ein Angebot anfordern.'
      },
      {
        icon: Building2,
        title: 'Dienstleistungsunternehmen',
        description: 'Von Facility bis Beratung: Wir erstellen klare und zuverlässige Websites.'
      },
      {
        icon: Users,
        title: 'Selbstständige & KMU',
        description: 'Ideal für Unternehmer, die schnell online sein wollen mit einer Website, die Vertrauen schafft.'
      }
    ]
  }
};

export const UseCasesSection = () => {
  const { language } = useLanguage();
  const t = content[language] || content.nl;

  return (
    <section 
      className="py-20 sm:py-28 bg-[#F9FAFB]"
      data-testid="usecases-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <span 
            className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4 bg-blue-100 text-[#2563EB]"
          >
            {t.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-[#111827] mb-6 leading-tight">
            {t.title}
          </h2>
          <p className="text-lg text-[#4B5563] leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {t.features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-white rounded-3xl p-8 border border-gray-200 hover:border-[#2563EB]/30 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-[#2563EB]"
                  style={{ boxShadow: '0 8px 20px rgba(37, 99, 235, 0.25)' }}
                >
                  <Icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-3">{feature.title}</h3>
                <p className="text-[#4B5563] leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
